'use client';
import React, { FC } from 'react';
import { t } from 'i18next';
import { FilterCheckbox } from 'components/common';
import {
  DemandeCongeStatut,
  DemandeType,
  ResponseStatsConge,
  TypeDemand,
} from 'features/conge/types/leaveRequestType';
import { DemandeConge, DemandesConge } from 'features/collaborateur/types';
import { downloadExcelBuffer } from 'utils/globalMethodes';
import {
  MintHrDropdown,
  MintHrDropdownItem,
} from 'components/common/MintHrDropdown';
import moment from 'moment';
import { ExportCongeService } from 'features/conge/services/leaveService';

interface LeaveRequestFilterProps {
  filters: DemandeType;
  setfilters: React.Dispatch<React.SetStateAction<DemandeType>>;
  selectedUsers: DemandeConge[];
  statistic: ResponseStatsConge | null;
  selectAll: boolean;
  excludedIds: string[];
  demandes: DemandesConge | null;
}

const LeaveRequestFilter: FC<LeaveRequestFilterProps> = ({
  setfilters,
  filters,
  selectedUsers,
  statistic,
  selectAll,
  excludedIds,
  demandes,
}) => {
  const filterCheckboxes = [
    {
      value: 1,
      label: 'Demande.Accepter',
      count: statistic?.accepted,
      color: 'success',
    },
    {
      value: 3,
      label: 'Demande.Rejete',
      count: statistic?.rejected,
      color: 'danger',
    },
    {
      value: 0,
      label: 'Demande.En_Cour',
      count: statistic?.demandeCount,
      color: 'warning',
    },
  ];

  const approvalOptions = [
    {
      id: TypeDemand.Approval,
      title: t('LeaveRequest.Filter.Approval', 'Mes Demandes'),
    },
    {
      id: TypeDemand.Permission,
      title: t('LeaveRequest.Filter.Permission', 'Autres Demandes'),
    },
  ];

  const selectedApproval = approvalOptions.find(
    (option) => option.id === filters.filters.approval,
  );

  const handleApprovalChange = (value: TypeDemand | null): void => {
    setfilters((prevState) => ({
      ...prevState,
      filters: {
        ...prevState.filters,
        approval: value,
      },
    }));
  };

  const handelExportData = async (all?: boolean): Promise<void> => {
    const response = await ExportCongeService({
      status: Array.isArray(filters.filters.status)
        ? filters.filters.status
            .map((status) => DemandeCongeStatut[status])
            .filter(Boolean)
            .join(',')
        : filters.filters.status,
      typeCongeIds: filters.filters.typeCongeIds || '',
      range_min: filters.filters.range_min?.toString() || '',
      range_max: filters.filters.range_max?.toString() || '',
      society: filters.filters.society || '',
      site: filters.filters.site || '',
      dateMin: filters.filters.dateMin || '',
      dateMax: filters.filters.dateMax || '',
      date_created_min: filters.filters.date_created_min || '',
      date_created_max: filters.filters.date_created_max || '',
      ids:
        selectAll || all
          ? ['all']
          : selectedUsers.map((user) => user.id.toString()),
      idsNotSelected: excludedIds,
    });
    downloadExcelBuffer(
      response,
      'time_off_list_' + moment().format('DD_MM_YYYY'),
    );
  };

  return (
    <>
      <div className="minthr-flex minthr-flex-items-center minthr-flex-space-between minthr-mb-6 minthr-mobile-mb-0 minthr-mobile-flex-wrap">
        <h4 className="minthr-mobile-mb-4">
          {t('DemandsNumber')}
          <span className="minthr-badge minthr-badge-primary minthr-badge-s minthr-ml-2">
            {statistic?.demandeCount}
          </span>
        </h4>
        {selectedUsers.length > 0 &&
          demandes?.data &&
          demandes.data.length > 0 && (
            <div className="ropdown minthr-dropdown minthr-ml-6 export-conge">
              <MintHrDropdown
                className=""
                title={
                  <button
                    className="dropdown-toggle minthr-btn minthr-px-2 minthr-btn-primary minthr-mobile-text-hide minthr-mobile-px-1 minthr-btn minthr-btn-outline minthr-btn-light-gray2"
                    type="button"
                  >
                    <img
                      className="minthr-mr-1 minthr-mobile-mr-0"
                      src="/icons/plus.svg"
                      alt=""
                    />
                    {t('Actions')}
                    <img
                      className="minthr-icon minthr-icon-xs minthr-ml-3 minthr-mobile-hide"
                      style={{ transform: 'rotate(90deg)' }}
                      src="/icons/arrow2.svg"
                      alt=""
                    />
                  </button>
                }
              >
                <MintHrDropdownItem
                  className="minthr-py-2 minthr-px-6"
                  onClick={() => {
                    handelExportData();
                  }}
                >
                  {t('Btn.ExporConges')}
                </MintHrDropdownItem>
                <MintHrDropdownItem
                  className="minthr-py-2 minthr-px-6"
                  onClick={() => {
                    handelExportData(true);
                  }}
                >
                  {t('Btn.ExportAllConges')}
                </MintHrDropdownItem>
              </MintHrDropdown>
            </div>
          )}

        <div className="minthr-flex minthr-flex-items-center minthr-mobile-flex-wrap minthr-ml-auto minthr-mobile-ml-0 minthr-mobile-mr-0 minthr-mobile-w-100 minthr-mobile-flex-space-between">
          <MintHrDropdown
            titleClassName=""
            className="minthr-icon-filter"
            title={
              <button
                type="button"
                className={`minthr-flex minthr-flex-items-center minthr-border minthr-badge minthr-px-3 minthr-py-1 minthr-mobile-w-100 ${
                  filters.filters.approval != null
                    ? 'minthr-badge-primary filter-active'
                    : ''
                }`}
              >
                <h5 className="minthr-color-dark minthr-line-height-30">
                  {selectedApproval
                    ? selectedApproval.title
                    : t('LeaveRequest.Filters.Sources', 'Type de demande')}
                </h5>
                {filters.filters.approval != null && (
                  <a
                    className="filter-close minthr-ml-auto"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleApprovalChange(null);
                    }}
                  >
                    <img
                      className="minthr-icon-dark"
                      src="/icons/close.svg"
                      alt=""
                    />
                  </a>
                )}
              </button>
            }
          >
            {approvalOptions.map((option) => (
              <MintHrDropdownItem
                key={option.id}
                className="minthr-py-2 minthr-px-6"
                onClick={() => handleApprovalChange(option.id)}
              >
                {option.title}
              </MintHrDropdownItem>
            ))}
          </MintHrDropdown>

          <span
            className="minthr-border-l minthr-ml-3 minthr-mr-0 minthr-mobile-hide"
            style={{ alignSelf: 'stretch' }}
          />

          {filterCheckboxes.map((checkbox) => (
            <FilterCheckbox
              key={checkbox.value}
              value={checkbox.value}
              label={t(checkbox.label)}
              count={checkbox.count || 0}
              color={checkbox.color}
              filterKey="status"
              filters={filters}
              setfilters={setfilters as (filters: unknown) => void}
              size="m"
            />
          ))}
        </div>
      </div>
    </>
  );
};

export default LeaveRequestFilter;
